import React, { useEffect, useMemo, useRef, useState } from "react";
import API_URL from "../apiConfig";
import { useNavigate } from "react-router-dom";

const PRODUCT_IMAGE = "/assets/images/products/zedro-membership.png";
const LANGUAGE_STORAGE_KEY = "zedro_academy_language";
const COOKIE_CONSENT_KEY = "cookieConsent";
const GA4_MEASUREMENT_ID = "G-6YC8SXPZSE";

function hasMarketingConsent() {
  if (typeof window === "undefined") return false;

  try {
    const storedConsent =
      window.localStorage.getItem(COOKIE_CONSENT_KEY);

    if (!storedConsent) return false;

    const parsedConsent = JSON.parse(storedConsent);

    return Boolean(parsedConsent?.prefs?.marketing);
  } catch {
    return false;
  }
}

function hasAnalyticsConsent() {
  if (typeof window === "undefined") return false;

  try {
    const storedConsent =
      window.localStorage.getItem(COOKIE_CONSENT_KEY);

    if (!storedConsent) return false;

    const parsedConsent = JSON.parse(storedConsent);

    return Boolean(parsedConsent?.prefs?.analytics);
  } catch {
    return false;
  }
}

function getGoogleTagValue(
  fieldName,
  timeoutMs = 1500
) {
  return new Promise((resolve) => {
    if (
      typeof window === "undefined" ||
      typeof window.gtag !== "function"
    ) {
      resolve("");
      return;
    }

    let completed = false;

    const timeout = window.setTimeout(() => {
      if (completed) return;

      completed = true;
      resolve("");
    }, timeoutMs);

    try {
      window.gtag(
        "get",
        GA4_MEASUREMENT_ID,
        fieldName,
        (value) => {
          if (completed) return;

          completed = true;
          window.clearTimeout(timeout);

          resolve(
            value === null || value === undefined
              ? ""
              : String(value)
          );
        }
      );
    } catch {
      window.clearTimeout(timeout);
      resolve("");
    }
  });
}

async function getGoogleAnalyticsContext() {
  const analyticsConsent =
    hasAnalyticsConsent();

  if (!analyticsConsent) {
    return {
      analyticsConsent: false,
      clientId: "",
      sessionId: "",
      pageLocation: "",
    };
  }

  const [clientId, sessionId] =
    await Promise.all([
      getGoogleTagValue("client_id"),
      getGoogleTagValue("session_id"),
    ]);

  return {
    analyticsConsent: true,
    clientId,
    sessionId,
    pageLocation: window.location.href,
  };
}

function getCookieValue(name) {
  if (typeof document === "undefined") return "";

  const prefix = `${name}=`;

  const cookie = document.cookie
    .split("; ")
    .find((item) => item.startsWith(prefix));

  if (!cookie) return "";

  const value = cookie.slice(prefix.length);

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function createMetaEventId(prefix) {
  if (typeof window === "undefined") {
    return `${prefix}_${Date.now()}`;
  }

  const uuid = window.crypto?.randomUUID?.();

  return uuid
    ? `${prefix}_${uuid}`
    : `${prefix}_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2)}`;
}

function getInitialLanguage() {
  if (typeof window === "undefined") return "es";

  const savedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return ["es", "fr"].includes(savedLanguage) ? savedLanguage : "es";
}

const COPY = {
  es: {
    languageLabel: "Idioma",
    heroTitle: "Tienda",
    heroText1: "Descubre la membresía de ZEDRO Academy.",
    heroText2:
      "Una experiencia de formación práctica con fundamento cristiano, creada para llevar lo aprendido a decisiones, acciones y avance real.",
    categories: ["Membresía"],
    empty: "No hay productos disponibles por ahora.",
    membership: "Membresía",
    details: "Ver detalles",
    startTrial: "Comenzar prueba",
    back: "← Volver a Tienda",
    productImageAlt: "Imagen de",
    priceFallback: "57 CAD / mes",
    taxNote:
      "Los impuestos aplicables, si corresponden, se calcularán y mostrarán antes de confirmar la suscripción en Stripe.",
    contentBadge: "Contenido principal en español",
    contentNoticeTitle: "Idioma de la experiencia",
    contentNoticeText:
      "La experiencia interna de la membresía se ofrece actualmente en español. Las páginas públicas y las condiciones esenciales están disponibles en español y francés.",
    cardText:
      "Una membresía de formación práctica con fundamento cristiano para desarrollar criterio, fortalecer decisiones y llevar lo aprendido a la vida diaria mediante recorridos guiados, micro-misiones, Bitácora ZEDRO y Conexión ZEDRO.",
    specsTitle: "Qué incluye",
    benefits: [
      "Acceso a los sistemas formativos activos de Cimientos ZEDRO.",
      "Recorridos guiados de 28 días, con formación diaria y avance progresivo.",
      "Micro-misiones para convertir lo aprendido en decisiones y acciones concretas.",
      "Bitácora ZEDRO para consultar respuestas, decisiones y evidencias de avance.",
      "Conexión ZEDRO para profundizar en pasajes bíblicos y preparar oraciones con claridad.",
      "Contenido formativo principal disponible actualmente en español.",
    ],
    conditionsAria: "Condiciones de compra",
    checkoutTitle: "Comienza con 7 días gratis",
    checkoutNote:
      "No se realizará ningún cobro durante los 7 días de prueba. Si no cancelas antes de finalizar la prueba gratuita, se activará automáticamente la membresía mensual según el precio mostrado antes de confirmar el pago.",
    languageNoticeTitle: "Idioma del contenido:",
    languageNotice:
      "La experiencia formativa de ZEDRO Academy se ofrece actualmente en español. La información legal, las condiciones de compra y los mensajes esenciales relacionados con pago, cancelación y cuenta están disponibles en español y francés.",
    emailLabel: "Correo electrónico de acceso",
    emailPlaceholder: "Tu correo electrónico",
    emailHelp:
      "Usaremos este correo para verificar si ya existe una cuenta, una membresía activa o una suscripción que deba renovarse.",
    reviewTitle: "Antes de confirmar:",
    reviewNotice:
      "serás dirigido a Stripe para revisar los datos de pago, el precio vigente, la moneda y cualquier impuesto aplicable, si corresponde, antes de completar la suscripción.",
    operatorTitle: "Operador:",
    operatorText:
      "ZEDRO Academy es una plataforma digital operada bajo el nombre comercial registrado Académie ZEDRO en Québec, Canadá.",
    supportTitle: "Soporte:",
    supportText:
      "para preguntas sobre membresía, pago, acceso o cancelación, puedes escribir a",
    legalTextStart:
      "Entiendo que ZEDRO Academy funciona con acceso progresivo, que no se realizará ningún cobro durante los 7 días de prueba y que, si no cancelo antes de finalizar la prueba gratuita, la membresía mensual se activará automáticamente. Acepto los",
    terms: "Términos y Condiciones",
    cancellationRefunds: "Política de Cancelación y Reembolsos",
    privacy: "Política de Privacidad",
    cookies: "Política de Cookies",
    and: "y la",
    continueStripe: "Continuar a Stripe",
    loading: "Preparando...",
    secure:
      "Pago seguro mediante Stripe. ZEDRO Academy no almacena números completos de tarjeta.",
    closeInfo: "Cerrar información",
    activeMemberTitle: "Ya eres miembro activo",
    activeMemberText: "Tu acceso a ZEDRO Academy está activo.",
    remainingDaysPrefix: "Te quedan",
    remainingDaysUnit: "días",
    remainingDaysSuffix: "de membresía.",
    dashboard: "Ir al Dashboard",
    close: "Cerrar",
    alerts: {
      emailRequired: "Ingresa tu correo para continuar.",
      legalRequired:
        "Antes de continuar, confirma que comprendes la prueba gratuita, la renovación automática, el acceso progresivo y las condiciones legales de ZEDRO Academy.",
      activeMember: "Este correo ya tiene una membresía activa. Puedes iniciar sesión.",
      checkoutError: "No se pudo iniciar la compra",
      genericCheckoutError: "Error iniciando compra",
      accessNotDetected: "Aún no se detecta tu membresía. Recarga la página.",
      accessError: "Error verificando acceso",
    },
  },
  fr: {
    languageLabel: "Langue",
    heroTitle: "Boutique",
    heroText1: "Découvrez l’abonnement de ZEDRO Academy.",
    heroText2:
      "Une expérience de formation pratique fondée sur des principes chrétiens, conçue pour transformer les apprentissages en décisions, en actions et en progrès concrets.",
    categories: ["Abonnement"],
    empty: "Aucun produit n’est disponible pour le moment.",
    membership: "Abonnement",
    details: "Voir les détails",
    startTrial: "Commencer l’essai",
    back: "← Retour à la boutique",
    productImageAlt: "Image de",
    priceFallback: "57 CAD / mois",
    taxNote:
      "Les taxes applicables, le cas échéant, seront calculées et affichées avant la confirmation de l’abonnement dans Stripe.",
    contentBadge: "Contenu principal en espagnol",
    contentNoticeTitle: "Langue de l’expérience",
    contentNoticeText:
      "L’expérience interne de l’abonnement est actuellement offerte en espagnol. Les pages publiques et les conditions essentielles sont disponibles en espagnol et en français.",
    cardText:
      "Un abonnement de formation pratique fondée sur des principes chrétiens, conçu pour développer le discernement, renforcer les décisions et mettre les apprentissages en pratique grâce à des parcours guidés, des micro-missions, Bitácora ZEDRO et Conexión ZEDRO.",
    specsTitle: "Ce qui est inclus",
    benefits: [
      "Accès aux systèmes de formation actifs de Fondements ZEDRO.",
      "Parcours guidés de 28 jours, avec formation quotidienne et progression structurée.",
      "Micro-missions pour transformer les apprentissages en décisions et en actions concrètes.",
      "Bitácora ZEDRO pour consulter les réponses, les décisions et les traces de progression.",
      "Conexión ZEDRO pour approfondir des passages bibliques et préparer des prières avec clarté.",
      "Le contenu principal de formation est actuellement offert en espagnol.",
    ],
    conditionsAria: "Conditions d’achat",
    checkoutTitle: "Essai gratuit de 7 jours",
    checkoutNote:
      "Aucun montant ne sera débité pendant les 7 jours d’essai. Si vous n’annulez pas avant la fin de l’essai gratuit, l’abonnement mensuel sera activé automatiquement selon le prix affiché avant la confirmation du paiement.",
    languageNoticeTitle: "Langue du contenu :",
    languageNotice:
      "L’expérience de formation de ZEDRO Academy est actuellement offerte en espagnol. Les informations légales, les conditions d’achat et les messages essentiels liés au paiement, à l’annulation et au compte sont disponibles en espagnol et en français.",
    emailLabel: "Courriel d’accès",
    emailPlaceholder: "Votre adresse courriel",
    emailHelp:
      "Nous utiliserons ce courriel pour vérifier s’il existe déjà un compte, un abonnement actif ou un abonnement à renouveler.",
    reviewTitle: "Avant de confirmer :",
    reviewNotice:
      "vous serez dirigé vers Stripe afin de vérifier les informations de paiement, le prix en vigueur, la devise et toute taxe applicable, le cas échéant, avant de compléter l’abonnement.",
    operatorTitle: "Opérateur :",
    operatorText:
      "ZEDRO Academy est une plateforme numérique exploitée sous le nom commercial enregistré Académie ZEDRO au Québec, Canada.",
    supportTitle: "Assistance :",
    supportText:
      "pour toute question concernant l’abonnement, le paiement, l’accès ou l’annulation, vous pouvez écrire à",
    legalTextStart:
      "Je comprends que ZEDRO Academy fonctionne avec un accès progressif, qu’aucun paiement ne sera effectué pendant les 7 jours d’essai et que, si je n’annule pas avant la fin de l’essai gratuit, l’abonnement mensuel sera activé automatiquement. J’accepte les",
    terms: "Conditions générales",
    cancellationRefunds: "Politique d’annulation et de remboursement",
    privacy: "Politique de confidentialité",
    cookies: "Politique de cookies",
    and: "et la",
    continueStripe: "Continuer vers Stripe",
    loading: "Préparation...",
    secure:
      "Paiement sécurisé par Stripe. ZEDRO Academy ne conserve pas les numéros complets de carte.",
    closeInfo: "Fermer l’information",
    activeMemberTitle: "Vous êtes déjà membre actif",
    activeMemberText: "Votre accès à ZEDRO Academy est actif.",
    remainingDaysPrefix: "Il vous reste",
    remainingDaysUnit: "jours",
    remainingDaysSuffix: "d’abonnement.",
    dashboard: "Aller au tableau de bord",
    close: "Fermer",
    alerts: {
      emailRequired: "Entrez votre courriel pour continuer.",
      legalRequired:
        "Avant de continuer, confirmez que vous comprenez l’essai gratuit, le renouvellement automatique, l’accès progressif et les conditions légales de ZEDRO Academy.",
      activeMember: "Ce courriel possède déjà un abonnement actif. Vous pouvez vous connecter.",
      checkoutError: "Impossible de commencer l’achat",
      genericCheckoutError: "Erreur lors du démarrage de l’achat",
      accessNotDetected: "Votre abonnement n’est pas encore détecté. Rechargez la page.",
      accessError: "Erreur lors de la vérification de l’accès",
    },
  },
};

const CONDITION_DETAILS = {
  es: {
    trial: {
      icon: "7",
      title: "7 días gratis",
      text:
        "Puedes iniciar con 7 días de prueba gratuita con tarjeta registrada. No se realizará ningún cobro durante la prueba. Si cancelas antes de finalizar los 7 días gratuitos, no se realizará el primer cobro mensual. Si no cancelas antes de finalizar la prueba, la membresía mensual se activará automáticamente según el precio mostrado antes de confirmar el pago.",
    },
    progressive: {
      icon: "↗",
      title: "Acceso progresivo",
      text:
        "Los sistemas, sus días y los recursos se habilitan por etapas para acompañar el proceso con estructura, ritmo y aplicación diaria.",
    },
    stripe: {
      icon: "✓",
      title: "Pago seguro",
      text:
        "El pago se procesa mediante Stripe. ZEDRO Academy no almacena números completos de tarjeta de crédito o débito.",
    },
    cancel: {
      icon: "↺",
      title: "Cancela cuando quieras",
      text:
        "Puedes cancelar tu membresía cuando quieras. La cancelación evita futuras renovaciones y futuros cobros. Si ya hubo un cobro mensual, tu acceso permanece activo hasta el final del periodo correspondiente.",
    },
    access: {
      icon: "⏱",
      title: "Acceso hasta fin del periodo",
      text:
        "Si cancelas después de un cobro mensual, conservarás el acceso hasta el final del periodo ya pagado, salvo que la ley aplicable exija o reconozca un derecho distinto.",
    },
    refunds: {
      icon: "i",
      title: "Sin reembolso proporcional",
      text:
        "Los pagos ya realizados no son reembolsables por cancelación anticipada del mes en curso y no se realizan reembolsos proporcionales por días no utilizados, salvo que la ley aplicable exija lo contrario.",
    },
  },
  fr: {
    trial: {
      icon: "7",
      title: "7 jours gratuits",
      text:
        "Vous pouvez commencer avec un essai gratuit de 7 jours avec une carte enregistrée. Aucun paiement ne sera effectué pendant l’essai. Si vous annulez avant la fin des 7 jours gratuits, le premier paiement mensuel ne sera pas effectué. Si vous n’annulez pas avant la fin de l’essai, l’abonnement mensuel sera activé automatiquement selon le prix affiché avant la confirmation du paiement.",
    },
    progressive: {
      icon: "↗",
      title: "Accès progressif",
      text:
        "Les systèmes, leurs journées et les ressources sont accessibles par étapes afin d’accompagner le parcours avec structure, rythme et mise en pratique quotidienne.",
    },
    stripe: {
      icon: "✓",
      title: "Paiement sécurisé",
      text:
        "Le paiement est traité par Stripe. ZEDRO Academy ne conserve pas les numéros complets de carte de crédit ou de débit.",
    },
    cancel: {
      icon: "↺",
      title: "Annulation libre",
      text:
        "Vous pouvez annuler votre abonnement à tout moment. L’annulation évite les renouvellements et paiements futurs. Si un paiement mensuel a déjà été effectué, votre accès reste actif jusqu’à la fin de la période correspondante.",
    },
    access: {
      icon: "⏱",
      title: "Accès jusqu’à la fin",
      text:
        "Si vous annulez après un paiement mensuel, vous conserverez l’accès jusqu’à la fin de la période déjà payée, sauf si la loi applicable exige ou reconnaît un droit différent.",
    },
    refunds: {
      icon: "i",
      title: "Sans prorata",
      text:
        "Les paiements déjà effectués ne sont pas remboursables en cas d’annulation anticipée du mois en cours et aucun remboursement proportionnel n’est effectué pour les jours non utilisés, sauf si la loi applicable l’exige autrement.",
    },
  },
};

const conditionOrder = [
  "trial",
  "progressive",
  "stripe",
  "cancel",
  "access",
  "refunds",
];

function isMembershipProduct(product) {
  const name = String(product?.name || "").toLowerCase();

  return (
    !name ||
    name.includes("membership") ||
    name.includes("membresía") ||
    name.includes("membresia") ||
    name.includes("abonnement")
  );
}

function getDisplayName(product, language = "es") {
  if (isMembershipProduct(product)) {
    return language === "fr"
      ? "ZEDRO Premium - Abonnement mensuel"
      : "ZEDRO Premium - Membresía Mensual";
  }

  return product?.name || "ZEDRO Academy";
}

function getDisplayPrice(product, language = "es") {
  if (isMembershipProduct(product)) {
    return COPY[language]?.priceFallback || "57 CAD / mes";
  }

  const rawPrice = product?.price_label || product?.display_price;

  if (rawPrice) return rawPrice;

  return COPY[language]?.priceFallback || "57 CAD / mes";
}

const MEMBERSHIP_PRICE_CAD = 57;

function getGoogleAnalyticsItem(product, language) {
  return {
    item_id: String(product?.id || "zedro-membership"),
    item_name: getDisplayName(product, language),
    item_category: "membership",
    price: MEMBERSHIP_PRICE_CAD,
    quantity: 1,
  };
}

function trackGoogleViewItem(product, language) {
  if (
    typeof window === "undefined" ||
    !hasAnalyticsConsent() ||
    typeof window.gtag !== "function" ||
    !product?.id
  ) {
    return;
  }

  window.gtag("event", "view_item", {
    send_to: GA4_MEASUREMENT_ID,
    currency: "CAD",
    value: MEMBERSHIP_PRICE_CAD,
    items: [
      getGoogleAnalyticsItem(product, language),
    ],
  });
}

function trackMetaViewContent(product, language) {
  if (
   typeof window === "undefined" ||
   !hasMarketingConsent() ||
   typeof window.fbq !== "function" ||
   !product?.id
 ) {
    return;
  }

  window.fbq("track", "ViewContent", {
    content_ids: [String(product.id)],
    content_type: "product",
    content_name: getDisplayName(
      product,
      language
    ),
    currency: "CAD",
  });
}

export default function Store() {
  const [language, setLanguage] = useState(getInitialLanguage);
  const [products, setProducts] = useState([]);
  const [email, setEmail] = useState("");
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [remainingDays, setRemainingDays] = useState(null);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const checkoutInProgressRef = useRef(false);

  const navigate = useNavigate();
  const copy = COPY[language];
  const conditionDetails = CONDITION_DETAILS[language];

  const currentCondition = useMemo(() => {
    return selectedCondition ? conditionDetails[selectedCondition] : null;
  }, [conditionDetails, selectedCondition]);

  const activeProduct = selectedProduct || products[0];

  useEffect(() => {
    fetch(`${API_URL}/api/store/products`)
      .then((res) => res.json())
      .then((data) => {
        if (data?.ok) setProducts(data.products || []);
      })
      .catch((err) => {
        console.error("Error cargando productos:", err);
      });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    function syncLanguage(nextLanguage) {
      if (["es", "fr"].includes(nextLanguage)) {
        setLanguage(nextLanguage);
      }
    }

    function handleLanguageEvent(event) {
      syncLanguage(event?.detail?.language);
    }

    function handleStorage(event) {
      if (event.key === LANGUAGE_STORAGE_KEY) {
        syncLanguage(event.newValue);
      }
    }

    syncLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
    window.addEventListener("zedro:languagechange", handleLanguageEvent);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("zedro:languagechange", handleLanguageEvent);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const handleCheckout = async (product) => {
    if (!email.trim()) {
      return alert(copy.alerts.emailRequired);
    }

    if (!acceptedLegal) {
      return alert(copy.alerts.legalRequired);
    }

    if (checkoutInProgressRef.current) {
      return;
    }

    checkoutInProgressRef.current = true;
    setIsCheckoutLoading(true);

    try {
      const marketingConsent = hasMarketingConsent();

    const googleAnalyticsContext =
      await getGoogleAnalyticsContext();

    const metaCheckoutEventId =
      createMetaEventId("initiate_checkout");

    const metaContext = {
      marketingConsent,

      fbp: marketingConsent
        ? getCookieValue("_fbp")
        : "",

      fbc: marketingConsent
        ? getCookieValue("_fbc")
        : "",

      eventSourceUrl: window.location.href,

      checkoutEventId: metaCheckoutEventId,
    };

    const res = await fetch(
      `${API_URL}/api/store/checkout`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          productId: product.id,
          email: email.trim(),
          meta: metaContext,
          analytics: googleAnalyticsContext,
        }),
      }
    );

    const data = await res.json();

    if (!res.ok) {
      if (
        data?.error ===
        "Ya tienes una membresía activa"
      ) {
        return alert(copy.alerts.activeMember);
      }

      return alert(
        data?.error || copy.alerts.checkoutError
      );
    }

    if (marketingConsent && typeof window.fbq === "function") {
      window.fbq(
        "track",
        "InitiateCheckout",
        {
          content_ids: [String(product.id)],
          content_type: "product",
          content_name: getDisplayName(
            product,
            language
          ),
          currency: "CAD",
        },
        {
          eventID: metaCheckoutEventId,
        }
      );
    }

      window.location.assign(data.url);
    } catch (err) {
      console.error(err);
      alert(copy.alerts.genericCheckoutError);
    } finally {
      checkoutInProgressRef.current = false;
      setIsCheckoutLoading(false);
    }
  };

  return (
    <div className="storePage">
      <style>{`
        .storePage {
          width: 100%;
          min-height: 100vh;
          background:
            radial-gradient(circle at top left, rgba(198, 168, 107, 0.09), transparent 34%),
            linear-gradient(180deg, #f7f8fa 0%, #eef3f6 100%);
          color: #17324d;
          padding: 2.5rem 1.5rem 4.5rem;
          box-sizing: border-box;
        }

        .storeShell {
          width: 100%;
          max-width: 1180px;
          margin: 0 auto;
        }

        .storeHero {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: start;
          gap: 1.5rem;
          margin: 0 auto 2rem;
        }

        .storeHeroCopy {
          max-width: 680px;
        }

        .storeHero h1 {
          margin: 0 0 0.9rem;
          color: #102b43;
          font-family: Questrial, sans-serif;
          font-size: clamp(1.9rem, 3vw, 2.6rem);
          line-height: 1.04;
          letter-spacing: -0.055em;
          font-weight: 500;
        }

        .storeHero p {
          margin: 0 0 0.45rem;
          max-width: 660px;
          color: rgba(16, 43, 67, 0.72);
          font-size: 1rem;
          line-height: 1.7;
        }

        .storeHeroSide {
          display: grid;
          justify-items: end;
          gap: 0.85rem;
          padding-top: 0.15rem;
        }

        .storeLanguageSwitch {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.24rem;
          border: 1px solid rgba(16, 43, 67, 0.12);
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.9);
          box-shadow: 0 10px 22px rgba(16, 43, 67, 0.04);
        }

        .storeLanguageBtn {
          min-width: 42px;
          min-height: 32px;
          padding: 0 0.8rem;
          border: 0;
          border-radius: 999px;
          background: transparent;
          color: rgba(16, 43, 67, 0.7);
          font-family: Questrial, sans-serif;
          font-size: 0.84rem;
          line-height: 1;
          font-weight: 900;
          cursor: pointer;
        }

        .storeLanguageBtn.isActive {
          background: #f7efd9;
          color: #102b43;
          box-shadow: 0 8px 18px rgba(198, 168, 107, 0.14);
        }

        .storeCategories {
          display: flex;
          justify-content: flex-end;
          flex-wrap: wrap;
          gap: 0.7rem;
        }

        .storeCategory {
          min-height: 40px;
          padding: 0 1.15rem;
          border-radius: 999px;
          border: 1px solid rgba(198, 168, 107, 0.72);
          background: rgba(255, 255, 255, 0.92);
          color: #17324d;
          font-size: 0.94rem;
          line-height: 1;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 10px 22px rgba(16, 43, 67, 0.04);
        }

        .storeCategory.isActive {
          background: #f7efd9;
          color: #102b43;
          border-color: #c6a86b;
          box-shadow: 0 12px 24px rgba(198, 168, 107, 0.14);
        }

        .storeEmpty {
          max-width: 620px;
          margin: 2rem auto 0;
          padding: 1.2rem;
          border-radius: 22px;
          background: #fff;
          color: rgba(16, 43, 67, 0.72);
          text-align: center;
          box-shadow: 0 14px 34px rgba(23, 50, 77, 0.06);
        }

        .storeListingGrid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 360px));
          gap: 1.25rem;
          align-items: stretch;
        }

        .storeCard {
          border: 1px solid rgba(16, 43, 67, 0.09);
          border-radius: 28px;
          background: rgba(255, 255, 255, 0.92);
          overflow: hidden;
          box-shadow: 0 18px 48px rgba(16, 43, 67, 0.08);
          transition: transform 0.18s ease, box-shadow 0.18s ease;
        }

        .storeCard:hover {
          transform: translateY(-3px);
          box-shadow: 0 22px 56px rgba(16, 43, 67, 0.12);
        }

        .storeCardImage {
          position: relative;
          height: 210px;
          background: #102b43;
          overflow: hidden;
        }

        .storeCardRibbon {
          position: absolute;
          left: 0.9rem;
          top: 0.9rem;
          z-index: 2;
          display: inline-flex;
          align-items: center;
          gap: 0.42rem;
          max-width: calc(100% - 1.8rem);
          padding: 0.46rem 0.68rem;
          border: 1px solid rgba(255, 255, 255, 0.28);
          border-radius: 999px;
          background:
            linear-gradient(135deg, rgba(255, 250, 238, 0.96), rgba(247, 239, 217, 0.92));
          color: #102b43;
          box-shadow: 0 14px 34px rgba(0, 0, 0, 0.18);
          backdrop-filter: blur(10px);
          font-size: 0.68rem;
          line-height: 1.15;
          font-weight: 900;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .storeCardRibbon::before {
          content: "";
          width: 7px;
          height: 7px;
          flex: 0 0 auto;
          border-radius: 999px;
          background: #c6a86b;
          box-shadow: 0 0 0 4px rgba(198, 168, 107, 0.18);
        }

        .storeCardImage img {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
        }

        .storeCardBody {
          padding: 1.15rem;
        }

        .storeProductKicker {
          margin: 0 0 0.55rem;
          color: #a27a32;
          font-size: 0.8rem;
          line-height: 1.25;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .storeCard h2 {
          margin: 0 0 0.45rem;
          color: #102b43;
          font-size: 1.35rem;
          line-height: 1.15;
          letter-spacing: -0.035em;
          font-weight: 500;
        }

        .storeCardPrice {
          margin: 0 0 0.7rem;
          color: #c6a86b;
          font-size: 0.96rem;
          font-weight: 800;
        }

        .storeCardTaxNote {
          margin: -0.35rem 0 0.75rem;
          color: rgba(16, 43, 67, 0.56);
          font-size: 0.78rem;
          line-height: 1.45;
        }

        .storeCardText {
          margin: 0 0 1.05rem;
          color: rgba(16, 43, 67, 0.72);
          font-size: 0.98rem;
          line-height: 1.65;
        }

        .storeCardActions {
          display: flex;
          gap: 0.7rem;
          flex-wrap: wrap;
        }

        .storeSecondaryBtn,
        .storeGhostBtn {
          min-height: 42px;
          padding: 0 1rem;
          border-radius: 999px;
          font-family: Questrial, sans-serif;
          font-size: 0.92rem;
          font-weight: 700;
          cursor: pointer;
          transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
        }

        .storeSecondaryBtn {
          border: 1px solid #c6a86b;
          background: #fff7e2;
          color: #102b43;
        }

        .storeGhostBtn {
          border: 1px solid rgba(16, 43, 67, 0.16);
          background: #fff;
          color: rgba(16, 43, 67, 0.78);
        }

        .storeSecondaryBtn:hover,
        .storeGhostBtn:hover {
          transform: translateY(-1px);
          box-shadow: 0 12px 24px rgba(16, 43, 67, 0.08);
        }

        .storeDetailTopBar {
          display: flex;
          justify-content: flex-end;
          margin: 0 0 0.85rem;
        }

        .storeBackBtn {
          min-height: 42px;
          padding: 0 1rem;
          border-radius: 999px;
          border: 1px solid rgba(198, 168, 107, 0.55);
          background: rgba(255, 255, 255, 0.82);
          color: #102b43;
          font-family: Questrial, sans-serif;
          font-size: 0.92rem;
          font-weight: 800;
          cursor: pointer;
          margin-bottom: 1.25rem;
        }

        .storeProduct {
          position: relative;
          display: grid;
          grid-template-columns: minmax(0, 1.05fr) minmax(330px, 0.95fr);
          gap: 1.2rem;
          padding: 1rem;
          border: 1px solid rgba(16, 43, 67, 0.09);
          border-radius: 34px;
          background: rgba(255, 255, 255, 0.88);
          box-shadow: 0 24px 70px rgba(16, 43, 67, 0.1);
          overflow: hidden;
        }

        .storeProductMedia {
          position: relative;
          isolation: isolate;
          min-height: 520px;
          border-radius: 28px;
          overflow: hidden;
          background:
            radial-gradient(circle at top right, rgba(198, 168, 107, 0.16), transparent 34%),
            linear-gradient(180deg, #143754 0%, #102c43 100%);
          padding: 1.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .storeMediaFrame {
          position: relative;
          z-index: 2;
          width: 100%;
          min-height: 470px;
          border-radius: 24px;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(198, 168, 107, 0.18);
          padding: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .storeDetailImage {
          width: 100%;
          height: 430px;
          display: block;
          object-fit: contain;
        }

        @media (min-width: 901px) {
          .storeProductMedia {
            align-items: flex-start;
            padding-top: 2rem;
          }

          .storeProductMedia::before {
            content: "Z";
            position: absolute;
            left: 50%;
            top: 54%;
            transform: translate(-50%, -50%);
            font-family: "Playfair Display", serif;
            font-size: clamp(19rem, 32vw, 34rem);
            line-height: 1;
            font-weight: 700;
            color: rgba(255, 255, 255, 0.035);
            z-index: 0;
            pointer-events: none;
            user-select: none;
          }

          .storeProductMedia::after {
            content: "";
            position: absolute;
            inset: 0;
            background:
              radial-gradient(circle at 50% 18%, rgba(198, 168, 107, 0.12), transparent 34%),
              radial-gradient(circle at 50% 82%, rgba(255, 255, 255, 0.045), transparent 36%);
            z-index: 1;
            pointer-events: none;
          }

          .storeMediaFrame {
            position: sticky;
            top: 112px;
            max-width: 560px;
          }
        }

        .storeProductInfo {
          padding: 1.25rem;
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        .storeProductInfo h2 {
          margin: 0 0 0.45rem;
          color: #102b43;
          font-family: Questrial, sans-serif;
          font-size: clamp(1.75rem, 3vw, 2.45rem);
          line-height: 1.08;
          letter-spacing: -0.055em;
          font-weight: 500;
        }

        .storePrice {
          margin: 0 0 1rem;
          color: #c6a86b;
          font-size: 1.04rem;
          line-height: 1.4;
          font-weight: 800;
        }

        .storeTaxNote {
          margin: -0.65rem 0 1rem;
          color: rgba(16, 43, 67, 0.56);
          font-size: 0.82rem;
          line-height: 1.45;
        }

        .storeContentNotice {
          margin: 0 0 1.05rem;
          padding: 0.9rem 0.95rem;
          border: 1px solid rgba(198, 168, 107, 0.34);
          border-radius: 20px;
          background:
            radial-gradient(circle at top left, rgba(198, 168, 107, 0.16), transparent 42%),
            linear-gradient(180deg, rgba(255, 250, 240, 0.96), rgba(255, 255, 255, 0.88));
          box-shadow: 0 16px 34px rgba(16, 43, 67, 0.07);
        }

        .storeContentNoticeLabel {
          display: inline-flex;
          align-items: center;
          gap: 0.42rem;
          margin: 0 0 0.42rem;
          color: #9a7634;
          font-size: 0.74rem;
          line-height: 1.2;
          font-weight: 900;
          letter-spacing: 0.09em;
          text-transform: uppercase;
        }

        .storeContentNoticeLabel::before {
          content: "";
          width: 7px;
          height: 7px;
          border-radius: 999px;
          background: #c6a86b;
          box-shadow: 0 0 0 4px rgba(198, 168, 107, 0.16);
        }

        .storeContentNotice p {
          margin: 0;
          color: rgba(16, 43, 67, 0.76);
          font-size: 0.9rem;
          line-height: 1.55;
        }

        .storeSpecsTitle {
          margin: 0.65rem 0 1.1rem;
          padding-top: 1rem;
          border-top: 1px solid rgba(16, 43, 67, 0.1);
          color: #102b43;
          font-family: Questrial, sans-serif;
          font-size: 1.12rem;
          line-height: 1.3;
          letter-spacing: 0.08em;
          text-align: center;
          text-transform: uppercase;
          font-weight: 800;
        }

        .storeBenefits {
          margin: 0 0 1.1rem;
          padding: 0;
          list-style: none;
          display: grid;
          gap: 0.68rem;
        }

        .storeBenefits li {
          display: grid;
          grid-template-columns: 26px 1fr;
          gap: 0.65rem;
          color: rgba(16, 43, 67, 0.78);
          font-size: 0.96rem;
          line-height: 1.55;
        }

        .storeBenefits li::before {
          content: "✓";
          width: 26px;
          height: 26px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: rgba(198, 168, 107, 0.14);
          color: #9a7634;
          font-weight: 800;
        }

        .storeConditions {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 0.62rem;
          margin: 0.15rem 0 1rem;
        }

        .storeConditionBtn {
          min-height: 64px;
          border: 1px solid rgba(16, 43, 67, 0.1);
          border-radius: 16px;
          background: #fff;
          color: #17324d;
          cursor: pointer;
          display: grid;
          grid-template-columns: 30px 1fr;
          align-items: center;
          gap: 0.52rem;
          text-align: left;
          padding: 0.58rem 0.62rem;
          box-shadow: 0 10px 20px rgba(16, 43, 67, 0.04);
          transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
        }

        .storeConditionBtn:hover {
          transform: translateY(-1px);
          border-color: rgba(198, 168, 107, 0.48);
          box-shadow: 0 14px 26px rgba(16, 43, 67, 0.08);
        }

        .storeConditionIcon {
          width: 30px;
          height: 30px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #f7efd9;
          color: #8b6b2f;
          font-weight: 900;
          font-size: 0.82rem;
        }

        .storeConditionText {
          color: rgba(16, 43, 67, 0.78);
          font-size: 0.78rem;
          line-height: 1.18;
          font-weight: 800;
        }

        .storeCheckout {
          margin-top: auto;
          padding: 1rem;
          border: 1px solid rgba(198, 168, 107, 0.24);
          border-radius: 22px;
          background: linear-gradient(180deg, #fffaf0, #ffffff);
        }

        .storeCheckoutTitle {
          margin: 0 0 0.4rem;
          color: #102b43;
          font-size: 1rem;
          line-height: 1.35;
          font-weight: 800;
        }

        .storeCheckoutNote {
          margin: 0 0 0.9rem;
          color: rgba(16, 43, 67, 0.7);
          font-size: 0.9rem;
          line-height: 1.55;
        }

        .storeLanguageNotice {
          margin: 0 0 0.95rem;
          padding: 0.75rem 0.85rem;
          border: 1px solid rgba(198, 168, 107, 0.22);
          border-radius: 16px;
          background: rgba(255, 255, 255, 0.72);
          color: rgba(16, 43, 67, 0.72);
          font-size: 0.9rem;
          line-height: 1.55;
        }

        .storeLanguageNotice p {
          margin: 0;
          font-size: 0.9rem;
          line-height: 1.55;
        }

        .storeLanguageNotice strong {
          color: #102b43;
          font-weight: 900;
        }

        .storeEmailBox {
          margin: 0 0 0.95rem;
          padding: 0.78rem 0.85rem;
          border: 1px solid rgba(198, 168, 107, 0.36);
          border-radius: 18px;
          background: rgba(255, 251, 241, 0.82);
          box-shadow: 0 14px 34px rgba(16, 43, 67, 0.07);
        }

        .storeEmailLabel {
          display: block;
          margin: 0 0 0.45rem;
          color: #102b43;
          font-size: 0.86rem;
          line-height: 1.35;
          font-weight: 900;
        }

        .storeEmailHelp {
          margin: 0.45rem 0 0;
          color: rgba(16, 43, 67, 0.62);
          font-size: 0.78rem;
          line-height: 1.45;
        }

        .storeInput {
          width: 100%;
          min-height: 48px;
          padding: 0 0.9rem;
          border-radius: 14px;
          border: 1px solid rgba(16, 43, 67, 0.14);
          background: #fff;
          color: #102b43;
          font-family: Questrial, sans-serif;
          font-size: 1rem;
          box-sizing: border-box;
          margin-bottom: 0.85rem;
        }

        .storeReviewNotice {
          margin: 0 0 0.95rem;
          padding: 0.75rem 0.85rem;
          border-radius: 16px;
          background: rgba(16, 43, 67, 0.045);
          color: rgba(16, 43, 67, 0.72);
          font-size: 0.82rem;
          line-height: 1.48;
        }

        .storeReviewNotice p {
          margin: 0;
          font-size: 0.82rem;
          line-height: 1.48;
        }

        .storeReviewNotice strong {
          color: #102b43;
          font-weight: 900;
        }

        .storeOperatorNotice {
          margin: 0 0 0.95rem;
          padding: 0.68rem 0.78rem;
          border-radius: 16px;
          background: rgba(198, 168, 107, 0.075);
          color: rgba(16, 43, 67, 0.72);
          font-size: 0.82rem;
          line-height: 1.48;
        }

        .storeOperatorNotice p {
          margin: 0;
          font-size: 0.82rem;
          line-height: 1.48;
        }

        .storeOperatorNotice p + p {
          margin-top: 0.65rem;
        }

        .storeOperatorNotice strong {
          color: #102b43;
          font-weight: 900;
        }

        .storeLegalCheck {
          display: grid;
          grid-template-columns: 24px 1fr;
          gap: 0.72rem;
          align-items: start;
          margin: 0 0 0.95rem;
          padding: 0.78rem 0.85rem;
          border: 1px solid rgba(16, 43, 67, 0.1);
          border-radius: 18px;
          background: rgba(255, 255, 255, 0.82);
          color: rgba(16, 43, 67, 0.72);
          font-size: 0.86rem;
          line-height: 1.55;
          cursor: pointer;
        }

        .storeLegalCheck input {
          width: 20px;
          height: 20px;
          margin: 3px 0 0;
          accent-color: #c6a86b;
          cursor: pointer;
        }

        .storeLegalCheck a {
          color: #17324d;
          font-weight: 800;
          text-decoration-color: rgba(198, 168, 107, 0.72);
          text-underline-offset: 3px;
        }

        .storeLegalCheck span {
          display: block;
        }

        .storePrimaryBtn {
          width: 100%;
          min-height: 50px;
          border: 1px solid #c6a86b;
          border-radius: 999px;
          background: linear-gradient(135deg, #f8e7ad, #d8a946);
          color: #163349;
          font-family: Questrial, sans-serif;
          font-size: 1rem;
          font-weight: 900;
          cursor: pointer;
          box-shadow: 0 16px 30px rgba(198, 168, 107, 0.22);
          transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
        }

        .storePrimaryBtn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 20px 36px rgba(198, 168, 107, 0.28);
        }

        .storePrimaryBtn:disabled {
          opacity: 0.52;
          cursor: not-allowed;
          box-shadow: none;
        }

        .storeSecure {
          margin: 0.75rem 0 0;
          color: rgba(16, 43, 67, 0.58);
          font-size: 0.78rem;
          line-height: 1.45;
          text-align: center;
        }

        .storeSecure p {
          margin: 0;
          font-size: 0.78rem;
          line-height: 1.45;
        }

        .storeInfoOverlay {
          position: fixed;
          inset: 0;
          z-index: 1000;
          background: rgba(8, 20, 31, 0.58);
          backdrop-filter: blur(4px);
          display: flex;
          justify-content: flex-end;
        }

        .storeInfoPanel {
          width: min(100%, 520px);
          min-height: 100%;
          padding: 2rem;
          background: #fff;
          color: #102b43;
          box-shadow: -24px 0 60px rgba(0, 0, 0, 0.18);
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
        }

        .storeInfoClose {
          align-self: flex-end;
          width: 44px;
          height: 44px;
          border-radius: 999px;
          border: 1px solid rgba(16, 43, 67, 0.14);
          background: #fff;
          color: #102b43;
          font-size: 1.35rem;
          cursor: pointer;
        }

        .storeInfoIcon {
          width: 58px;
          height: 58px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #f7efd9;
          color: #8b6b2f;
          font-size: 1.2rem;
          font-weight: 900;
          margin-bottom: 1.1rem;
        }

        .storeInfoPanel h2 {
          margin: 0 0 0.75rem;
          font-size: clamp(1.8rem, 4vw, 2.55rem);
          line-height: 1.06;
          letter-spacing: -0.03em;
        }

        .storeInfoPanel p {
          margin: 0;
          color: rgba(16, 43, 67, 0.74);
          font-size: 1.02rem;
          line-height: 1.75;
        }

        @media (max-width: 900px) {
          .storePage {
            padding: 2.3rem 1rem 3.8rem;
          }

          .storeHero {
            grid-template-columns: 1fr;
            align-items: start;
          }

          .storeHeroSide {
            justify-items: start;
          }

          .storeDetailTopBar {
            justify-content: flex-start;
          }

          .storeCategories {
            justify-content: flex-start;
          }

          .storeProduct {
            grid-template-columns: 1fr;
          }

          .storeProductMedia {
            min-height: auto;
          }

          .storeConditions {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }

        @media (max-width: 560px) {
          .storePage {
            padding: 2rem 1rem 3.5rem;
          }

          .storeHero h1 {
            font-size: clamp(2rem, 10vw, 2.55rem);
          }

          .storeHero p {
            font-size: 1rem;
          }

          .storeListingGrid {
            grid-template-columns: 1fr;
          }

          .storeCardImage {
            height: 185px;
          }

          .storeProduct {
            border-radius: 28px;
            padding: 0.75rem;
          }

          .storeProductInfo {
            padding: 0.85rem;
          }

          .storeProductMedia {
            padding: 0.85rem;
          }

          .storeMediaFrame {
            min-height: 260px;
          }

          .storeDetailImage {
            height: 230px;
          }

          .storeConditions {
            grid-template-columns: 1fr;
          }

          .storeConditionBtn {
            min-height: 58px;
          }

          .storeConditionText {
            font-size: 0.88rem;
          }

          .storeInfoPanel {
            padding: 1.35rem;
          }
        }
      `}</style>

      <div className="storeShell">
        {!activeProduct || !selectedProduct ? (
          <>
            <section className="storeHero">
              <div className="storeHeroCopy">
                <h1>{copy.heroTitle}</h1>

                <p>{copy.heroText1}</p>

                <p>{copy.heroText2}</p>
              </div>

              <div className="storeHeroSide">
                <div className="storeCategories" aria-label={copy.conditionsAria}>
                  {copy.categories.map((category, index) => (
                    <span
                      key={category}
                      className={`storeCategory${index === 0 ? " isActive" : ""}`}
                    >
                      {category}
                    </span>
                  ))}
                </div>
              </div>
            </section>

            {products.length === 0 && <div className="storeEmpty">{copy.empty}</div>}

            <div className="storeListingGrid">
              {products.map((product) => (
                <article key={product.id} className="storeCard">
                  <div className="storeCardImage">
                    <span className="storeCardRibbon">{copy.contentBadge}</span>

                    <img
                      src={PRODUCT_IMAGE}
                      alt={`${copy.productImageAlt} ${getDisplayName(product, language)}`}
                    />
                  </div>

                  <div className="storeCardBody">
                    <p className="storeProductKicker">{copy.membership}</p>

                    <h2>{getDisplayName(product, language)}</h2>

                    <p className="storeCardPrice">
                      {getDisplayPrice(product, language)}
                    </p>

                    <p className="storeCardTaxNote">{copy.taxNote}</p>

                    <p className="storeCardText">{copy.cardText}</p>

                    <div className="storeCardActions">
                      <button
                        type="button"
                        className="storeSecondaryBtn"
                        onClick={() => {
                          trackMetaViewContent(product, language);
                          trackGoogleViewItem(product, language);

                          setSelectedProduct(product);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        {copy.details}
                      </button>

                      <button
                        type="button"
                        className="storeGhostBtn"
                        onClick={() => {
                          trackMetaViewContent(product, language);
                          trackGoogleViewItem(product, language);

                          setSelectedProduct(product);
                          setTimeout(() => {
                            document
                              .querySelector(".storeCheckout")
                              ?.scrollIntoView({ behavior: "smooth", block: "center" });
                          }, 80);
                        }}
                      >
                        {copy.startTrial}
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </>
        ) : (
          <>
            <button
              type="button"
              className="storeBackBtn"
              onClick={() => {
                setSelectedProduct(null);
                setSelectedCondition(null);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              {copy.back}
            </button>

            <article className="storeProduct">
              <div className="storeProductMedia">
                <div className="storeMediaFrame">
                  <img
                    className="storeDetailImage"
                    src={PRODUCT_IMAGE}
                    alt={`${copy.productImageAlt} ${getDisplayName(activeProduct, language)}`}
                  />
                </div>
              </div>

              <div className="storeProductInfo">
                <p className="storeProductKicker">{copy.membership}</p>

                <h2>{getDisplayName(activeProduct, language)}</h2>

                <p className="storePrice">
                  {getDisplayPrice(activeProduct, language)}
                </p>

                <p className="storeTaxNote">{copy.taxNote}</p>

                <div className="storeContentNotice">
                  <span className="storeContentNoticeLabel">
                    {copy.contentNoticeTitle}
                  </span>

                  <p>{copy.contentNoticeText}</p>
                </div>

                <h3 className="storeSpecsTitle">{copy.specsTitle}</h3>

                <ul className="storeBenefits">
                  {copy.benefits.map((benefit) => (
                    <li key={benefit}>{benefit}</li>
                  ))}
                </ul>

                <div className="storeConditions" aria-label={copy.conditionsAria}>
                  {conditionOrder.map((key) => {
                    const item = conditionDetails[key];

                    return (
                      <button
                        key={key}
                        type="button"
                        className="storeConditionBtn"
                        onClick={() => setSelectedCondition(key)}
                      >
                        <span className="storeConditionIcon">{item.icon}</span>
                        <span className="storeConditionText">{item.title}</span>
                      </button>
                    );
                  })}
                </div>

                <div className="storeCheckout">
                  <p className="storeCheckoutTitle">{copy.checkoutTitle}</p>

                  <p className="storeCheckoutNote">{copy.checkoutNote}</p>

                  <div className="storeLanguageNotice">
                    <p>
                      <strong>{copy.languageNoticeTitle}</strong>{" "}
                      {copy.languageNotice}
                    </p>
                  </div>

                  <div className="storeEmailBox">
                    <label className="storeEmailLabel" htmlFor="checkout-email">
                      {copy.emailLabel}
                    </label>

                    <input
                      id="checkout-email"
                      className="storeInput"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      placeholder={copy.emailPlaceholder}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />

                    <p className="storeEmailHelp">{copy.emailHelp}</p>
                  </div>

                  <div className="storeReviewNotice">
                    <p>
                      <strong>{copy.reviewTitle}</strong> {copy.reviewNotice}
                    </p>
                  </div>

                  <div className="storeOperatorNotice">
                    <p>
                      <strong>{copy.operatorTitle}</strong> {copy.operatorText}
                    </p>

                    <p>
                      <strong>{copy.supportTitle}</strong> {copy.supportText}{" "}
                      <a href="mailto:support@zedro.academy">
                        support@zedro.academy
                      </a>
                      .
                    </p>
                  </div>

                  <label className="storeLegalCheck">
                    <input
                      type="checkbox"
                      checked={acceptedLegal}
                      onChange={(e) => setAcceptedLegal(e.target.checked)}
                    />

                    <span>
                      {copy.legalTextStart}{" "}
                      <a href="/legal/terminos" target="_blank" rel="noreferrer">
                        {copy.terms}
                      </a>
                      , la{" "}
                      <a
                        href="/legal/cancelacion-reembolsos"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {copy.cancellationRefunds}
                      </a>
                      , la{" "}
                      <a href="/legal/privacidad" target="_blank" rel="noreferrer">
                        {copy.privacy}
                      </a>{" "}
                        {copy.and}{" "}
                      <a href="/legal/cookies" target="_blank" rel="noreferrer">
                        {copy.cookies}
                      </a>
                      .
                      </span>
                  </label>

                  <button
                    type="button"
                    className="storePrimaryBtn"
                    onClick={() => handleCheckout(activeProduct)}
                    disabled={
                      isCheckoutLoading ||
                      !email.trim() ||
                      !acceptedLegal
                    }
                  >
                    {
                      isCheckoutLoading
                        ? copy.loading
                        : copy.continueStripe
                    }
                  </button>

                  <div className="storeSecure">
                    <p>{copy.secure}</p>
                  </div>
                </div>
              </div>
            </article>
          </>
        )}
      </div>

      {currentCondition && (
        <div
          className="storeInfoOverlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="store-info-title"
          onClick={() => setSelectedCondition(null)}
        >
          <aside
            className="storeInfoPanel"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="storeInfoClose"
              onClick={() => setSelectedCondition(null)}
              aria-label={copy.closeInfo}
            >
              ×
            </button>

            <div style={{ marginTop: "auto", marginBottom: "auto" }}>
              <span className="storeInfoIcon">{currentCondition.icon}</span>
              <h2 id="store-info-title">{currentCondition.title}</h2>
              <p>{currentCondition.text}</p>
            </div>
          </aside>
        </div>
      )}

      {showMembershipModal && (
        <div className="zedro-modal-overlay">
          <div className="zedro-modal">
            <h2>{copy.activeMemberTitle}</h2>

            <p>
              {copy.activeMemberText}
              {remainingDays !== null && (
                <>
                  <br />
                  {copy.remainingDaysPrefix} {" "}
                  <strong>
                    {remainingDays} {copy.remainingDaysUnit}
                  </strong>{" "}
                  {copy.remainingDaysSuffix}
                </>
              )}
            </p>

            <div className="zedro-modal-actions">
              <button
                onClick={async () => {
                  try {
                    const res = await fetch(`${API_URL}/api/me`, {
                      headers: {
                        Authorization:
                          "Bearer " +
                          JSON.parse(localStorage.getItem("zedro_session"))
                            ?.token,
                      },
                    });

                    const data = await res.json();

                    if (data?.subscriptions?.length) {
                      navigate("/dashboard");
                    } else {
                      alert(copy.alerts.accessNotDetected);
                    }
                  } catch (err) {
                    console.error(err);
                    alert(copy.alerts.accessError);
                  }
                }}
              >
                {copy.dashboard}
              </button>

              <button
                className="secondary"
                onClick={() => setShowMembershipModal(false)}
              >
                {copy.close}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
